import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Users, QrCode, Siren, ShieldAlert, Scan, Search, MessageSquareWarning, Quote, Check, X, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";

const ROLES = [
  "user", "premium", "volunteer", "corporate", "fleet_manager", "ngo",
  "hospital", "police", "ambulance", "support", "admin", "super_admin", "developer",
];

const STATUS_STYLE = {
  sent: "bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))]",
  mock: "bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]",
  failed: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))]",
  pending: "bg-muted text-foreground",
};

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [sms, setSms] = useState({ twilio_ready: false, logs: [] });
  const [testis, setTestis] = useState([]);
  const [testiFilter, setTestiFilter] = useState("pending");

  const load = async () => {
    try {
      const [s, u, sm, tt] = await Promise.all([
        api.get("/admin/stats"),
        api.get(`/admin/users${q ? `?q=${encodeURIComponent(q)}` : ""}`),
        api.get("/admin/sms-log"),
        api.get(`/admin/testimonials?status=${testiFilter}`),
      ]);
      setStats(s.data);
      setUsers(u.data);
      setSms(sm.data);
      setTestis(tt.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load admin data");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [q, testiFilter]);

  const moderate = async (id, action) => {
    try {
      await api.post(`/admin/testimonials/${id}/${action}`);
      toast.success(`Testimonial ${action}d`);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const removeTesti = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Deleted");
    load();
  };

  const updateRole = async (id, role) => {
    try {
      await api.post(`/admin/users/${id}/role`, null, { params: { role } });
      toast.success("Role updated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const CARDS = stats && [
    { label: "Users", value: stats.users, icon: Users, color: "primary" },
    { label: "QR profiles", value: stats.qr_profiles, icon: QrCode, color: "secondary" },
    { label: "Active SOS", value: stats.sos_active, icon: Siren, color: "destructive" },
    { label: "Total SOS", value: stats.sos_total, icon: Siren, color: "accent" },
    { label: "Community reports", value: stats.community, icon: ShieldAlert, color: "primary" },
    { label: "QR scans", value: stats.scans, icon: Scan, color: "secondary" },
  ];

  return (
    <div className="max-w-6xl">
      <div className="mb-8">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-2">Admin Console</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl tracking-tighter">Platform overview</h1>
        <p className="text-muted-foreground mt-1">Real-time metrics across the Guardian360 network.</p>
      </div>

      {CARDS && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-8">
          {CARDS.map((c, i) => (
            <motion.div key={c.label} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="rounded-2xl border border-border/70 bg-card p-4">
              <div className={`w-9 h-9 rounded-xl mb-2 grid place-items-center bg-[hsl(var(--${c.color}))]/10`}>
                <c.icon className={`w-4 h-4 text-[hsl(var(--${c.color}))]`} />
              </div>
              <div className="font-display font-black text-2xl tracking-tighter">{c.value}</div>
              <div className="text-xs text-muted-foreground">{c.label}</div>
            </motion.div>
          ))}
        </div>
      )}

      <Tabs defaultValue="users">
        <TabsList className="mb-4">
          <TabsTrigger value="users" data-testid="tab-users">Users</TabsTrigger>
          <TabsTrigger value="sms" data-testid="tab-sms">
            <MessageSquareWarning className="w-3.5 h-3.5 mr-1.5" />
            SMS Fallback ({sms.logs?.length || 0})
          </TabsTrigger>
          <TabsTrigger value="testimonials" data-testid="tab-testimonials">
            <Quote className="w-3.5 h-3.5 mr-1.5" /> Testimonials ({testis.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          <div className="rounded-2xl border border-border/70 bg-card overflow-hidden">
            <div className="p-4 border-b border-border/60 flex items-center gap-3">
              <div className="font-display font-bold text-lg tracking-tight">Users</div>
              <div className="flex-1 max-w-md relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input data-testid="admin-user-search" className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email..." />
              </div>
            </div>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {users.map((u) => (
                    <TableRow key={u.id} data-testid={`admin-user-row-${u.id}`}>
                      <TableCell className="font-medium">{u.name}</TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Select value={u.role} onValueChange={(v) => updateRole(u.id, v)}>
                          <SelectTrigger className="w-40 h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="sms">
          <div className="rounded-2xl border border-border/70 bg-card overflow-hidden">
            <div className="p-4 border-b border-border/60 flex items-center gap-3 justify-between flex-wrap">
              <div>
                <div className="font-display font-bold text-lg tracking-tight">SMS fallback log</div>
                <div className="text-xs text-muted-foreground">Auto-fires 60 s after SOS if no responder is assigned</div>
              </div>
              <Badge className={sms.twilio_ready
                ? "bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]"
                : "bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))] text-white"}
                data-testid="twilio-status">
                {sms.twilio_ready ? "Twilio LIVE" : "MOCK MODE (add TWILIO_* env vars to go live)"}
              </Badge>
            </div>
            {sms.logs?.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">
                No SMS fallback events yet.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>To</TableHead>
                      <TableHead>Body</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Provider SID</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sms.logs.map((r) => (
                      <TableRow key={r.id} data-testid={`sms-log-${r.id}`}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</TableCell>
                        <TableCell className="font-mono text-xs">{r.to}</TableCell>
                        <TableCell className="max-w-md truncate text-sm">{r.body}</TableCell>
                        <TableCell>
                          <Badge className={`uppercase text-[10px] tracking-wider ${STATUS_STYLE[r.status] || STATUS_STYLE.pending}`}>{r.status}</Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground font-mono">{r.provider_sid || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="testimonials">
          <div className="rounded-2xl border border-border/70 bg-card overflow-hidden">
            <div className="p-4 border-b border-border/60 flex items-center gap-3 justify-between flex-wrap">
              <div>
                <div className="font-display font-bold text-lg tracking-tight">User testimonials</div>
                <div className="text-xs text-muted-foreground">Approved entries appear on the landing page automatically.</div>
              </div>
              <div className="flex gap-2">
                {["pending", "approved", "rejected"].map((s) => (
                  <button
                    key={s}
                    data-testid={`testi-filter-${s}`}
                    onClick={() => setTestiFilter(s)}
                    className={`text-xs px-3 py-1.5 rounded-full uppercase tracking-wider transition-colors ${testiFilter === s ? "bg-foreground text-background" : "bg-muted text-muted-foreground hover:text-foreground"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            {testis.length === 0 ? (
              <div className="p-10 text-center text-sm text-muted-foreground">
                No {testiFilter} testimonials.
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {testis.map((t) => (
                  <div key={t.id} className="p-5 flex gap-4 items-start" data-testid={`testi-row-${t.id}`}>
                    <div className="w-10 h-10 rounded-xl bg-[hsl(var(--accent))]/10 grid place-items-center shrink-0">
                      <Quote className="w-4 h-4 text-[hsl(var(--accent))]" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <div className="font-semibold text-sm">{t.name}</div>
                        {t.city && <div className="text-xs text-muted-foreground">· {t.city}</div>}
                        <div className="ml-2 text-[10px] text-muted-foreground">{"★".repeat(t.stars || 5)}</div>
                      </div>
                      <div className="text-sm text-foreground/80 leading-relaxed line-clamp-4">"{t.quote}"</div>
                      <div className="text-[10px] text-muted-foreground uppercase tracking-wider mt-2">{new Date(t.created_at).toLocaleString()}</div>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      {t.status !== "approved" && (
                        <Button data-testid={`approve-testi-${t.id}`} size="sm" onClick={() => moderate(t.id, "approve")} className="bg-[hsl(var(--secondary))] hover:bg-[hsl(var(--secondary))]/90 text-white">
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {t.status !== "rejected" && (
                        <Button data-testid={`reject-testi-${t.id}`} size="sm" variant="outline" onClick={() => moderate(t.id, "reject")}>
                          <X className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      <Button data-testid={`delete-testi-${t.id}`} size="sm" variant="ghost" onClick={() => removeTesti(t.id)} className="text-[hsl(var(--destructive))]">
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

      </Tabs>
    </div>
  );
}
